import {
  DropDownList as KendoDropDownList,
  DropDownListBlurEvent,
  DropDownListChangeEvent,
  DropDownListCloseEvent,
  DropDownListFocusEvent,
  DropDownListOpenEvent,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import { classNames } from '@progress/kendo-react-common';
import Field from '../../../layout/field/Field';
import * as _ from 'lodash';
import {
  PUZZLE_DROP_DOWN_LIST_PROPS
} from './Types';
import WithTooltip from '../../../../utility/withTooltip';
import PuzzleTooltip from '../../tooltip/PuzzleTooltip';
import Container from '../../../layout/container/Container';
import { Label } from '@progress/kendo-react-labels';
import { 
  ABSTRACT_FIELD_OMITTED_PROPS, 
  FIELD_OMITTED_PROPS, 
  TOOLTIP_OMITTED_PROPS
} from '../../../../utility';

/**
 * For the layout and design purposes the entire Kendo DropDownList needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes
 * @author Stevan Stojanovic
 */
class PuzzleDropDownList extends PureComponent<PUZZLE_DROP_DOWN_LIST_PROPS> {
  dropdownRef: any = null;
  tooltip: any = null;
  dropdownID: any = null;
  otherTooltip: any = null;

  static defaultProps = {
    tooltipOpenDelay: 1000,
    tooltipAnchor: 'target',
    size: 'medium'
  };

  componentDidMount() {
    this.dropdownID = this.props.id ? this.props.id : this.props.id ? this.props.id : this.dropdownRef?.base?.guid;
  }

  /**
   * Storing Kendo's DropDownList components events
   * Manages triggering events outside the component(where we instantiate)
   * as well as handling internal state updates
   * */
  private events = {
    onBlur: (event: DropDownListBlurEvent) => {
      this.triggerEvent('onBlur', event);
    },
    onFocus: (event: DropDownListFocusEvent) => {
      this.triggerEvent('onFocus', event);
    },
    onOpen: (event: DropDownListOpenEvent) => {
      this.triggerEvent('onOpen', event);
    },
    onClose: (event: DropDownListCloseEvent) => {
      this.triggerEvent('onClose', event);
    },
    onChange: (event: DropDownListChangeEvent) => {
      this.triggerEvent('onChange', event);
    }
  };

  /**
   *  Custom Value dropdownlist render
   * @param element
   * @param value
   */
  private valueRender = (element: React.ReactElement<HTMLSpanElement>, value: any) => {
    const {
      withTooltip,
      titleText,
      tooltipAnchor,
      tooltipOpenDelay,
      tooltipPosition,
      tooltipContent,
      tooltipClassName = ''
    } = this.props;

    let text = value;
    if (value && this.props.textField) {
      text = value[this.props.textField];
    }

    let kendoDefaultValueRender = React.cloneElement(element, {
      ...element.props
    }, [<React.Fragment
      key={'value-1'}
    >{text}</React.Fragment>]);

    let iconWithToolTipRender = (
      <>
        <WithTooltip
          className={`tp-dropdown-list-info-icon-wrap k-d-flex k-align-items-center k-justify-content-center ${tooltipClassName}`}
          context={this}
          title={titleText}
          htmlTag={'span'}
        >
          <i
            tabIndex={0}
            className="tp-icon tp-icon-sm tp-info tp-color-accent tp-round"
            aria-hidden={'true'}
            title={titleText}
          />
        </WithTooltip>
        <PuzzleTooltip
          ref={(el: any) => this.tooltip = el}
          anchorElement={tooltipAnchor}
          openDelay={tooltipOpenDelay}
          position={tooltipPosition}
          content={tooltipContent}
        />
      </>
    );

    if (withTooltip) {
      return (<>
        {kendoDefaultValueRender}
        {iconWithToolTipRender}
      </>);
    } else {
      return kendoDefaultValueRender;
    }
  };

  /**
   *  Custom Item dropdownlist render
   * @param li
   * @param itemProps
   */
  private itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    let {
      dataItem,
      index
    } = itemProps;

    let text = dataItem;
    if (dataItem && this.props.textField) {
      text = dataItem[this.props.textField];
    }

    const {
      tooltipAnchor,
      tooltipOpenDelay,
      tooltipPosition,
      tooltipContent,
      withTooltipItems,
      tooltipClassName = '',
      customTooltipText,
      tooltipCalloutClassName,
      withTooltip
    } = this.props;

    const itemChildren = (
      <>
        {text}
        <Container flex1 />
        <WithTooltip
          context={this}
          title={text}
          htmlTag={'span'}
          className={`k-d-flex k-align-items-center ${tooltipClassName}`}
          tooltipRef={withTooltip ? this.otherTooltip : this.tooltip}
          closeOnClick
        >
          <i
            tabIndex={0}
            className="tp-icon tp-icon-sm tp-info tp-color-accent tp-round"
            aria-hidden={'true'}
            title={customTooltipText ? customTooltipText[index] : text}
          />
        </WithTooltip>
        <PuzzleTooltip
          // TODO This is a quick fix that should be removed with a GLDIS-1298 ticket
          ref={(el: any) => withTooltip ? this.otherTooltip = el : this.tooltip = el}
          anchorElement={tooltipAnchor}
          openDelay={tooltipOpenDelay}
          position={tooltipPosition}
          content={tooltipContent}
          className={tooltipCalloutClassName}
        />
      </>
    );

    const modifiedProps = {
      ...li.props,
      className: `${li.props.className} ${dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : ''}`
    };

    let kendoDefaultItemRender = React.cloneElement(li, modifiedProps, [<span
      className={`k-list-item-text ${dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : ''}`}
      key={'data-1'}
    >{text}</span>]);

    let additionalPropsForItem = {
      ...li.props,
      className: `${li.props.className} tp-list-item-with-tooltip ${dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : ''}`
    };
    let withToolTipItemRender = React.cloneElement(li, additionalPropsForItem, itemChildren);

    if (withTooltipItems) {
      return withToolTipItemRender;
    } else {
      return kendoDefaultItemRender;
    }
  }

  render() {
    const {
      asField,
      ariaDescribedBy,
      disabled,
      errorTxt,
      className = '',
      labelClassName = '',
      label,
      size,
      value,
      valid,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      dataCy
    } = this.props;

    const dropdownOmittedProps = [].concat(ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS, TOOLTIP_OMITTED_PROPS);
    const dropdownProps = _.omit(this.props, dropdownOmittedProps);

    const fieldCls = classNames(
      {
        [className]: className,
        'k-small': size === 'small',
        'k-large': size === 'large',
        'k-medium': size === 'medium',
        'k-state-empty': !value,
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled
      }
    );

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const content = (
      <div
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
        className={`k-d-flex-col ${fieldCls}`}
      >
        {label && <Label
          editorId={this.dropdownID}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >{label}
        </Label>}
        <KendoDropDownList
          id={this.dropdownID}
          data-cy={dataCy ? dataCy : undefined}
          ref={(dropdown: any) => this.dropdownRef = dropdown}
          {...dropdownProps}
          {...this.events}
          valueRender={this.valueRender}
          itemRender={this.itemRender}
          popupSettings={{
            popupClass: 'data-preventrecorddeselect'
          }}
        />
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }

  /**
   * Helper method for triggering events outside the cmp itself
   * */
  private triggerEvent(eventType: string, event: any) {
    if (this.props[eventType]) {
      this.props[eventType].call(undefined, {
        ...event,
        target: this.dropdownRef
      });
    }
  }
}

export default PuzzleDropDownList;
